import { createFileRoute, notFound } from "@tanstack/react-router";
import BlogPost from "@/pages/BlogPost";
import { POST_FAQS } from "@/data/postFaqs";
import { fetchBlogPost } from "@/lib/blogQueries";
import { buildHead, SITE_URL, absoluteUrl } from "@/components/SEO";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await fetchBlogPost(params.slug);
    // Unknown slug: a genuine 404. Redirecting an article to the /blog hub is
    // read by search engines as a soft 404, so never do that here.
    if (!post) throw notFound();
    return { post };
  },

  head: ({ loaderData, params }) => {
    const post = loaderData?.post;
    if (!post) return buildHead({ path: `/blog/${params.slug}`, noindex: true, title: "Unavailable | GoHub Blog" });

    const url = `${SITE_URL}/blog/${post.slug}`;
    const title = post.seo_title || `${post.title} | GoHub Blog`;
    const description = post.seo_description || post.excerpt || "";
    const image = absoluteUrl(post.featured_image_url);

    const jsonLd: Record<string, unknown>[] = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.seo_title || post.title,
        description,
        ...(image ? { image } : {}),
        ...(post.published_at ? { datePublished: post.published_at } : {}),
        dateModified: post.updated_at || post.published_at || undefined,
        author: { "@type": "Organization", name: "GoHub", url: SITE_URL },
        publisher: { "@type": "Organization", name: "GoHub", url: SITE_URL },
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["article h1", ".prose"],
        },
      },
    ];

    const faqs = POST_FAQS[post.slug];
    if (faqs?.length) {
      jsonLd.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }

    return buildHead({
      path: `/blog/${post.slug}`,
      title,
      description,
      canonical: url,
      ogType: "article",
      ogImage: image,
      jsonLd,
      breadcrumbs: [
        { name: "Home", url: SITE_URL },
        { name: "Blog", url: `${SITE_URL}/blog` },
        { name: post.title, url },
      ],
    });
  },
  component: BlogPost,
});
